import { useEffect, useState } from 'react';

/**
 * Marks a non-production instance, on every page including login.
 *
 * The identity is asked for at RUNTIME (`GET /instance`, served by the API that
 * serves this bundle) rather than baked in with a `VITE_*` variable, because the
 * review instance must run byte-identical assets to production — that is what
 * makes approving a change there mean anything. See `api/src/services/instance.ts`.
 *
 * Production answers `{ label: null }` and nothing below renders: no banner, no
 * title change, no layout shift. Only the instance that is not production changes.
 */
export default function InstanceBanner() {
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch('/instance', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { label: null }))
      .then((data) => {
        if (live && typeof data?.label === 'string' && data.label) setLabel(data.label);
      })
      .catch(() => {
        /* An instance that cannot say what it is stays unmarked. */
      });
    return () => {
      live = false;
    };
  }, []);

  // Reserve the bar's height (styles/globals.css) and name the instance in the
  // tab title, so a screenshot says which instance it came from even if the bar
  // itself is cropped out.
  useEffect(() => {
    if (!label) return;
    const title = document.title;
    document.title = `[${label}] ${title}`;
    document.body.classList.add('instance-marked');
    return () => {
      document.title = title;
      document.body.classList.remove('instance-marked');
    };
  }, [label]);

  if (!label) return null;

  return (
    <div
      role="status"
      aria-label={`${label} instance — not production`}
      className="fixed inset-x-0 top-0 z-[100] flex h-9 items-center justify-center gap-3 overflow-hidden bg-ember-500 px-3 text-night-950 shadow-[0_2px_12px_-2px_rgba(0,0,0,0.8)]"
      style={{
        backgroundImage:
          'repeating-linear-gradient(135deg, rgba(0,0,0,0.16) 0 10px, transparent 10px 20px)',
      }}
    >
      <span className="ident shrink-0 rounded bg-night-950 px-2 py-0.5 text-xs font-bold text-ember-400">
        {label}
      </span>
      <span className="truncate text-xs font-bold uppercase tracking-wider sm:text-sm">
        {label} instance · not the live logbook
      </span>
    </div>
  );
}
